import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Clock, MapPin, Trash2, CalendarDays } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Navbar } from '../../components/Navbar';
import { Switch } from '../../components/ui/switch';
import { DeliverySlotRow } from '../../components/DeliverySlotRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/delivery-slots/config`;
const opts = { withCredentials: true };
const err = (e, f) => toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || f);

function dayName(offset) {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  d.setDate(d.getDate() + offset);
  return offset === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function DaysCard({ days, onChange }) {
  return (
    <section className="border border-[#2A0F3D] bg-[#0C0612] p-4" data-testid="slot-days-card">
      <h2 className="font-mono text-sm text-[#39FF14] font-bold flex items-center gap-2 mb-1"><CalendarDays className="w-4 h-4" /> BOOKING_DAYS</h2>
      <p className="font-mono text-[11px] text-[#6B8E73] mb-3">// Today is always bookable (future hours only). Turn off days you don't want customers booking ahead.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {days.map((on, i) => (
          <label key={i} className="flex items-center justify-between gap-2 border border-[#2A0F3D] px-3 py-2 font-mono text-xs text-[#A3C7A8]">
            <span>+{i + 1} · {dayName(i + 1)}</span>
            <Switch checked={on} onCheckedChange={(v) => onChange(days.map((x, j) => (j === i ? v : x)))} data-testid={`slot-day-toggle-${i + 1}`} />
          </label>
        ))}
      </div>
    </section>
  );
}

function ZipChip({ z, fee, onRemove, onSetFee }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(fee ?? '');
  const save = async (e) => {
    e.preventDefault();
    if (val !== '' && (Number(val) < 0 || Number(val) > 500)) { toast.error('Enter an amount from $0 to $500'); return; }
    if (await onSetFee(z, val === '' ? null : Number(val))) setEditing(false);
  };
  return (
    <span className="inline-flex items-center gap-1.5 border border-[#FF9F0A]/60 text-[#FF9F0A] font-mono text-xs px-2 py-1" data-testid={`ld-zip-${z}`}>
      {z}
      {editing ? (
        <form onSubmit={save} className="inline-flex items-center gap-1">
          <span>$</span>
          <input type="number" min="0" max="500" step="0.5" value={val} onChange={(e) => setVal(e.target.value)} placeholder="slot fee" autoFocus
            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
            className="bg-[#050003] border border-[#2A0F3D] focus:border-[#39FF14] text-white text-xs px-1 w-24 outline-none placeholder:text-[#4B6B52] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none" data-testid={`ld-zip-fee-input-${z}`} />
          <button type="submit" className="text-[#39FF14]" data-testid={`ld-zip-fee-save-${z}`}>✓</button>
          <button type="button" onClick={() => setEditing(false)} className="text-[#6B8E73] hover:text-white" aria-label="Cancel" data-testid={`ld-zip-fee-cancel-${z}`}>✕</button>
        </form>
      ) : (
        <button type="button" onClick={() => { setVal(fee ?? ''); setEditing(true); }} className="text-[#A3C7A8] hover:text-[#39FF14] underline decoration-dotted"
          data-testid={`ld-zip-fee-${z}`}>{fee != null ? `$${Number(fee).toFixed(2)}` : 'slot fee'}</button>
      )}
      <button type="button" onClick={() => onRemove(z)} className="hover:text-[#FF3B30]" aria-label={`Remove ${z}`} data-testid={`ld-zip-remove-${z}`}><Trash2 className="w-3 h-3" /></button>
    </span>
  );
}

function ZipCard({ zips, fees, onAdd, onRemove, onSetFee }) {
  const [zip, setZip] = useState('');
  const add = async (e) => { e.preventDefault(); if (await onAdd(zip)) setZip(''); };
  return (
    <section className="border border-[#2A0F3D] bg-[#0C0612] p-4" data-testid="ld-zip-card">
      <h2 className="font-mono text-sm text-[#39FF14] font-bold flex items-center gap-2 mb-1"><MapPin className="w-4 h-4" /> LONG_DISTANCE_ZIPCODES</h2>
      <p className="font-mono text-[11px] text-[#6B8E73] mb-3">// Orders to these zipcodes pay a long distance fee (tier waiver applies). Tap the amount to give a zip its own price — it replaces the slot&apos;s long distance fee in every slot. &quot;slot fee&quot; = use each slot&apos;s long distance fee.</p>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="5-digit zip" inputMode="numeric"
          className="bg-[#050003] border border-[#2A0F3D] focus:border-[#39FF14] font-mono text-sm px-2 py-1 text-white outline-none w-32" data-testid="ld-zip-input" />
        <button type="submit" disabled={zip.length !== 5} className="px-3 py-1 font-mono text-xs border border-[#39FF14] text-[#39FF14] disabled:opacity-40" data-testid="ld-zip-add">+ Add</button>
      </form>
      {zips.length === 0 ? (
        <p className="font-mono text-xs text-[#6B8E73]" data-testid="ld-zip-empty">No long distance zipcodes yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="ld-zip-list">
          {zips.map((z) => <ZipChip key={z} z={z} fee={fees?.[z]} onRemove={onRemove} onSetFee={onSetFee} />)}
        </div>
      )}
    </section>
  );
}

export default function DeliverySlotsPage() {
  const { user } = useAuth();
  const isSA = user?.role === 'super_admin';
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(isSA ? null : user?.region);
  const [cfg, setCfg] = useState(null);
  const [copyVer, setCopyVer] = useState(0);

  useEffect(() => {
    if (!isSA) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/regions`, opts).then(({ data }) => {
      const list = (data.regions || []).map((r) => r.code);
      setRegions(list);
      setRegion((r) => r || list[0]);
    }).catch((e) => err(e, 'Could not load regions'));
  }, [isSA]);

  const load = useCallback(async () => {
    if (!region) return;
    try { setCfg((await axios.get(`${API}/${region}`, opts)).data); } catch (e) { err(e, 'Could not load slots'); }
  }, [region]);
  useEffect(() => { setCfg(null); load(); }, [load]);

  const patchSlot = async (hour, body) => {
    try {
      const { data } = await axios.patch(`${API}/${region}/slots/${hour}`, body, opts);
      setCfg((c) => ({ ...c, slots: c.slots.map((s) => (s.hour === hour ? data : s)) }));
      toast.success('Slot saved');
    } catch (e) { err(e, 'Save failed'); throw e; }
  };
  const copyPricing = async (hour, to_hours) => {
    try {
      const { data } = await axios.post(`${API}/${region}/slots/${hour}/copy`, { to_hours }, opts);
      setCfg((c) => ({ ...c, slots: data.slots }));
      setCopyVer((v) => v + 1);
      toast.success(`Pricing copied to ${data.copied_to.length} slot${data.copied_to.length === 1 ? '' : 's'}`);
    } catch (e) { err(e, 'Copy failed'); throw e; }
  };
  const setDays = async (days_enabled) => {
    try { await axios.patch(`${API}/${region}/days`, { days_enabled }, opts); setCfg((c) => ({ ...c, days_enabled })); } catch (e) { err(e, 'Save failed'); }
  };
  const addZip = async (zipcode) => {
    try { const { data } = await axios.post(`${API}/${region}/zipcodes`, { zipcode }, opts); setCfg((c) => ({ ...c, ...data })); return true; } catch (e) { err(e, 'Invalid zipcode'); return false; }
  };
  const removeZip = async (z) => {
    try { const { data } = await axios.delete(`${API}/${region}/zipcodes/${z}`, opts); setCfg((c) => ({ ...c, ...data })); } catch (e) { err(e, 'Remove failed'); }
  };
  const setZipFee = async (z, amount) => {
    try {
      const { data } = await axios.put(`${API}/${region}/zipcodes/${z}/fee`, { amount }, opts);
      setCfg((c) => ({ ...c, ...data }));
      toast.success(amount == null ? `${z} uses each slot's long distance fee` : `${z} long distance set to $${amount.toFixed(2)}`);
      return true;
    } catch (e) { err(e, 'Save failed'); return false; }
  };

  const activeCount = cfg?.slots.filter((s) => s.active).length || 0;
  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="delivery-slots-page">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        <Link to="/dispatch" className="inline-flex items-center gap-2 text-[#39FF14] font-mono text-sm"><ArrowLeft className="w-4 h-4" /> BACK_TO_DISPATCH</Link>
        <div>
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold flex items-center gap-3"><Clock className="w-6 h-6" /> {'>'} DELIVERY_SLOTS</h1>
          <p className="font-mono text-xs text-[#6B8E73] mt-1">// Hourly slots in Eastern Time. Inactive slots are never shown to customers.</p>
        </div>
        {cfg && (
          <div className={`border px-3 py-2 font-mono text-xs ${cfg.flag_enabled ? 'border-[#39FF14] text-[#39FF14]' : 'border-[#FF9F0A] text-[#FF9F0A]'}`} data-testid="slot-flag-status">
            {cfg.flag_enabled ? '● LIVE — customers pick a slot at checkout.' : '○ OFF — checkout unchanged. Configure freely; turn on `customer.scheduled_delivery` in Settings when ready.'}
          </div>
        )}
        {isSA && regions.length > 1 && (
          <div className="flex gap-2" data-testid="slot-region-tabs">
            {regions.map((r) => (
              <button key={r} type="button" onClick={() => setRegion(r)} className={`px-3 py-1.5 font-mono text-xs uppercase border ${r === region ? 'border-[#39FF14] text-[#39FF14]' : 'border-[#2A0F3D] text-[#6B8E73]'}`} data-testid={`slot-region-${r}`}>{r}</button>
            ))}
          </div>
        )}
        {!cfg ? <p className="font-mono text-xs text-[#6B8E73]">Loading…</p> : (
          <>
            <DaysCard days={cfg.days_enabled} onChange={setDays} />
            <section className="space-y-2" data-testid="slot-list">
              <h2 className="font-mono text-sm text-[#39FF14] font-bold">HOURLY_SLOTS · {region?.toUpperCase()} · <span data-testid="slot-active-count">{activeCount}</span>/24 ACTIVE</h2>
              {cfg.slots.map((s) => (
                <DeliverySlotRow key={`${region}-${s.hour}-${copyVer}`} slot={s} tiers={cfg.tiers} slots={cfg.slots} onCopy={copyPricing}
                  onSave={patchSlot} onToggle={(h, v) => patchSlot(h, { active: v }).catch(() => {})} />
              ))}
            </section>
            <ZipCard zips={cfg.long_distance_zips} fees={cfg.long_distance_zip_fees} onAdd={addZip} onRemove={removeZip} onSetFee={setZipFee} />
          </>
        )}
      </div>
    </div>
  );
}
